const initializeHomeBody = function(scope) {
    const context = scope && scope.querySelectorAll ? scope : document;
    const blocks = context.matches && context.matches('.home-body')
        ? [context]
        : context.querySelectorAll('.home-body');

    blocks.forEach(function(block) {
        if (block.dataset.homeBodyInitialized === 'true') {
            return;
        }

        block.dataset.homeBodyInitialized = 'true';
        const carousel = block.querySelector('.carousel');
        const track = block.querySelector('.carousel-track');
        const originalCards = Array.from(block.querySelectorAll('.carousel-card'));
        const mobileQuery = window.matchMedia('(max-width: 960px)');

        // `cards` holds every card in the track, clones included, in DOM order.
        let cards = originalCards.slice();
        let isDragging = false;

        originalCards.forEach(function(card, index) {
            card.dataset.cardIndex = String(index);
        });

        const setCardState = function(card, isActive) {
            const toggle = card.querySelector('.card-toggle');
            const symbol = toggle ? toggle.querySelector('span') : null;

            card.classList.toggle('is-active', isActive);

            if (toggle) {
                toggle.setAttribute('aria-expanded', isActive ? 'true' : 'false');
            }

            if (symbol) {
                symbol.textContent = mobileQuery.matches && isActive ? '−' : '+';
            }
        };

        // Applies a state to a card and every clone of it so all sets stay the same width.
        const setIndexState = function(cardIndex, isActive) {
            cards.forEach(function(card) {
                if (card.dataset.cardIndex === cardIndex) {
                    setCardState(card, isActive);
                }
            });
        };

        const setResponsiveState = function() {
            cards.forEach(function(card) {
                setCardState(card, !mobileQuery.matches && card.dataset.cardIndex === '0');
            });
        };

        setResponsiveState();
        mobileQuery.addEventListener('change', setResponsiveState);

        if (!carousel || !track) {
            return;
        }

        /* ------------------------------------------------------------------
         * Infinite loop
         *
         * The original cards are surrounded by cloned sets on both sides. The
         * user can always keep going in either direction, and whenever the
         * scroll position comes to rest inside a cloned set it is shifted by
         * whole set widths back into the original set. Because every set has
         * identical widths (states are mirrored across clones) the shift is
         * invisible.
         * ---------------------------------------------------------------- */
        const canLoop = originalCards.length > 1;
        let isLooping = false;
        let leadClone = null; // clone of the first card in the set directly before the originals

        const getPosition = function(element) {
            return element.offsetLeft - track.offsetLeft;
        };

        const getRealStart = function() {
            return getPosition(originalCards[0]);
        };

        const getSetWidth = function() {
            return leadClone ? getRealStart() - getPosition(leadClone) : 0;
        };

        const refreshCards = function() {
            cards = Array.from(track.querySelectorAll('.carousel-card'));
        };

        const createClone = function(card) {
            const clone = card.cloneNode(true);

            clone.classList.add('is-clone');
            clone.setAttribute('aria-hidden', 'true');
            clone.querySelectorAll('a, button').forEach(function(element) {
                element.setAttribute('tabindex', '-1');
            });

            return clone;
        };

        const addSet = function(prepend) {
            const fragment = document.createDocumentFragment();

            originalCards.forEach(function(card) {
                fragment.appendChild(createClone(card));
            });

            if (prepend) {
                track.insertBefore(fragment, track.firstChild);
            } else {
                track.appendChild(fragment);
            }
        };

        // Makes sure there are enough cloned sets on each side to fill the
        // viewport plus one extra set, so a drag or settle can never reveal
        // the end of the track before the position is normalised.
        const ensureLoopCoverage = function() {
            if (!canLoop) {
                return;
            }

            if (!leadClone) {
                addSet(true);
                leadClone = track.firstElementChild;
                addSet(false);
                refreshCards();
            }

            const setWidth = getSetWidth();

            if (setWidth <= 0) {
                // Not laid out yet (hidden); try again on the next resize.
                return;
            }

            const perSet = originalCards.length;
            const needed = Math.ceil(carousel.clientWidth / setWidth) + 1;
            let before = Math.round(getRealStart() / setWidth);
            let after = cards.length / perSet - before - 1;
            let prepended = 0;

            while (before < needed) {
                addSet(true);
                before++;
                prepended++;
            }

            while (after < needed) {
                addSet(false);
                after++;
            }

            refreshCards();

            if (!isLooping) {
                isLooping = true;
                carousel.scrollLeft = getRealStart();
            } else if (prepended) {
                // Keep the view where it was after content was inserted before it.
                carousel.scrollLeft += prepended * setWidth;
            }
        };

        // Shifts the scroll position back into the original set when it has
        // drifted into a cloned set. Returns the number of sets shifted by.
        const normalizeLoop = function() {
            if (!isLooping) {
                return 0;
            }

            const setWidth = getSetWidth();
            const realStart = getRealStart();

            if (setWidth <= 0) {
                return 0;
            }

            // Browsers round scroll offsets to whole pixels while card
            // positions can be fractional, so allow a pixel of slack.
            const tolerance = 1;
            const current = carousel.scrollLeft;
            let sets = 0;

            if (current < realStart - tolerance) {
                sets = Math.ceil((realStart - current) / setWidth);
            } else if (current >= realStart + setWidth - tolerance) {
                sets = -Math.floor((current - realStart + tolerance) / setWidth);
            }

            if (sets !== 0) {
                carousel.scrollLeft = current + sets * setWidth;
            }

            return sets;
        };

        ensureLoopCoverage();

        let resizeTimeout = null;
        window.addEventListener('resize', function() {
            window.clearTimeout(resizeTimeout);
            resizeTimeout = window.setTimeout(ensureLoopCoverage, 200);
        });

        /* ------------------------------------------------------------------
         * Drag, settle and keyboard navigation
         * ---------------------------------------------------------------- */
        let startX = 0;
        let startScrollLeft = 0;
        let lastX = 0;
        let lastTime = 0;
        let velocity = 0;
        let startCardIndex = 0;
        let settleTimeout = null;
        let idleTimeout = null;

        const clampIndex = function(index) {
            return Math.max(0, Math.min(cards.length - 1, index));
        };

        const getClosestCardIndex = function(scrollPosition) {
            let closestIndex = 0;
            let closestDistance = Infinity;

            cards.forEach(function(card, index) {
                const distance = Math.abs(getPosition(card) - scrollPosition);

                if (distance < closestDistance) {
                    closestDistance = distance;
                    closestIndex = index;
                }
            });

            return closestIndex;
        };

        // Called once the carousel has come to rest (after a smooth scroll,
        // a native scroll, or when there was nothing to scroll).
        const finishSettling = function() {
            window.clearTimeout(settleTimeout);
            window.clearTimeout(idleTimeout);

            if (isDragging) {
                return;
            }

            normalizeLoop();
            carousel.classList.remove('is-settling');
        };

        const scrollToCard = function(index) {
            const card = cards[clampIndex(index)];

            if (!card) {
                return;
            }

            const targetScroll = getPosition(card);

            carousel.classList.remove('is-dragging');
            carousel.classList.add('is-settling');
            window.clearTimeout(settleTimeout);

            if (Math.abs(targetScroll - carousel.scrollLeft) < 1) {
                finishSettling();
                return;
            }

            carousel.scrollTo({ left: targetScroll, behavior: 'smooth' });

            // Safety net in case no scroll events arrive.
            settleTimeout = window.setTimeout(finishSettling, 1500);
        };

        const settleCarousel = function() {
            const projectedScroll = carousel.scrollLeft - velocity * 240;
            const travelledDistance = carousel.scrollLeft - startScrollLeft;
            let targetCardIndex = getClosestCardIndex(projectedScroll);

            if (travelledDistance > 40) {
                targetCardIndex = Math.max(targetCardIndex, startCardIndex + 1);
            } else if (travelledDistance < -40) {
                targetCardIndex = Math.min(targetCardIndex, startCardIndex - 1);
            } else {
                targetCardIndex = startCardIndex;
            }

            scrollToCard(targetCardIndex);
        };

        carousel.addEventListener('scroll', function() {
            if (isDragging) {
                return;
            }

            window.clearTimeout(idleTimeout);
            idleTimeout = window.setTimeout(finishSettling, 150);
        });

        carousel.addEventListener('pointerdown', function(event) {
            if (event.target.closest('a, button')) {
                return;
            }

            event.preventDefault();
            isDragging = true;
            startX = event.clientX;
            startScrollLeft = carousel.scrollLeft;
            startCardIndex = getClosestCardIndex(startScrollLeft);
            lastX = event.clientX;
            lastTime = performance.now();
            velocity = 0;
            window.clearTimeout(settleTimeout);
            window.clearTimeout(idleTimeout);
            carousel.classList.remove('is-settling');
            carousel.classList.add('is-dragging');
            carousel.setPointerCapture(event.pointerId);
        });

        carousel.addEventListener('pointermove', function(event) {
            if (!isDragging) {
                return;
            }

            const currentTime = performance.now();
            const elapsed = currentTime - lastTime;

            carousel.scrollLeft = startScrollLeft - (event.clientX - startX);

            // If the drag crossed into a cloned set, jump back by whole sets
            // and keep the drag origin in step so the finger stays "attached".
            const shiftedSets = normalizeLoop();

            if (shiftedSets !== 0) {
                startScrollLeft += shiftedSets * getSetWidth();
                startCardIndex += shiftedSets * originalCards.length;
            }

            if (elapsed > 0) {
                velocity = (event.clientX - lastX) / elapsed;
            }

            lastX = event.clientX;
            lastTime = currentTime;
        });

        carousel.addEventListener('pointerup', function(event) {
            if (!isDragging) {
                return;
            }

            isDragging = false;

            if (carousel.hasPointerCapture(event.pointerId)) {
                carousel.releasePointerCapture(event.pointerId);
            }

            settleCarousel();
        });

        carousel.addEventListener('pointercancel', function() {
            if (!isDragging) {
                return;
            }

            isDragging = false;
            settleCarousel();
        });

        carousel.addEventListener('keydown', function(event) {
            if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') {
                return;
            }

            event.preventDefault();
            const currentIndex = getClosestCardIndex(carousel.scrollLeft);
            scrollToCard(currentIndex + (event.key === 'ArrowRight' ? 1 : -1));
        });

        /* ------------------------------------------------------------------
         * Card toggles (delegated so clones added later are covered too)
         * ---------------------------------------------------------------- */
        track.addEventListener('click', function(event) {
            const toggle = event.target.closest('.card-toggle');
            const card = toggle ? toggle.closest('.carousel-card') : null;

            if (!card || !track.contains(card)) {
                return;
            }

            const cardIndex = card.dataset.cardIndex;
            const shouldOpen = mobileQuery.matches
                ? !card.classList.contains('is-active')
                : true;

            cards.forEach(function(item) {
                setCardState(item, false);
            });

            if (!shouldOpen) {
                return;
            }

            setIndexState(cardIndex, true);

            if (!mobileQuery.matches) {
                card.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
                return;
            }

            // On mobile align the opened card to the start of the carousel so it
            // has room to grow, then re-align once the width transition ends in
            // case a collapsing card before it shifted its position.
            scrollToCard(cards.indexOf(card));

            const realign = function(transitionEvent) {
                if (transitionEvent.target !== card) {
                    return;
                }

                card.removeEventListener('transitionend', realign);

                if (!isDragging && card.classList.contains('is-active')) {
                    scrollToCard(cards.indexOf(card));
                }
            };

            card.addEventListener('transitionend', realign);
        });
    });
};

document.addEventListener('DOMContentLoaded', function() {
    initializeHomeBody(document);

    if (window.acf) {
        window.acf.addAction('render_block_preview/type=home-body', initializeHomeBody);
    }
});
